// ── Step 2: Keyword Library & Search Terms Module ────────────────
// Owns: step-keywords panel DOM, reactive table rendering, ST builder.

import {
  getState,
  setState,
  setStateBulk,
  subscribe,
} from './state.js';
import { normalizeSearchTermsDraft } from './listing-draft-payload.js';
import { filterSelectedKeywordsForSearchTerms } from './keyword-policy-client.js';
import {
  escapeHtml,
  normalizeText,
  formatVolume,
  parseAsinsFromText,
  getErrorMessage,
} from './utils.js';
import {
  MAX_SEARCH_TERMS_BYTES,
} from './constants.js';
import { keywordFacade } from './interaction/facades/keyword.facade.instance.js';
import { listingFacade } from './interaction/facades/listing.facade.instance.js';
import { batchFacade } from './interaction/facades/batch.facade.instance.js';
const { preCheckKeywords, translateSearchTerms, marketDataKeywords } = keywordFacade;
const { previewBatchKeywordStrategy } = batchFacade;
import { getMaxNavigableStep } from './navigation-bounds.js';
import { packKeywordsByBytes, updateByteCounter } from './byte-counter.js';
import { renderKeywordChips, amazonSearchUrl as sharedAmazonSearchUrl } from './keyword-chips.js';
import { getMarketDataKeywords, extractKeywordText, extractSearchVolume } from './keyword-lookup.js';
import { renderKeywordSummaryInto } from './modules/listing/step2/keyword-summary-view.js';
import { renderKeywordStrategyView } from './modules/listing/step2/keyword-strategy-view.js';
import { downloadKeywordStrategyWorkbook } from './step2/keyword-strategy-export.js';
import { compactKeywordUsePoliciesForStep2 } from './step2/keyword-strategy-request.js';
import { buildAutoSelectedSearchTerms } from './step2-search-terms-auto.js';
import { showToast } from './toast.js';
import { computeVirtualWindow } from './interaction/ui/virtual-window.js';
import {
  closeModalDialog,
  hideModalStatus,
  openModalDialog,
  showModalStatus,
} from './interaction/ui/modal-dialog.js';
import {
  createKeywordPrecheckController,
  removeConflictingKeywords,
  resolveKeywordConflicts,
} from './step2/keyword-precheck-controller.js';
import { createSearchTermsSyncController } from './step2/search-terms-sync-controller.js';
import { createSearchTermsCandidateFilterController } from './step2/search-terms-candidate-filter-controller.js';
import { createSearchTermsQuickSelectionController } from './step2/search-terms-quick-selection-controller.js';
import { createMarketDataKeywordController } from './step2/market-data-keyword-controller.js';
import { createBusinessContextController } from './step2/business-context-controller.js';
import { createSearchTermsAutoSelectionController } from './step2/search-terms-auto-selection-controller.js';
import { renderKeywordTableView } from './presentation/steps/step2/keyword-table-view.js';
import { createSearchTermsOutputController } from './step2/search-terms-output-controller.js';

export { removeConflictingKeywords, resolveKeywordConflicts };

// ── DOM Helpers ─────────────────────────────────────────────────

const $ = (sel) => document.querySelector(sel);
const $$ = (sel) => document.querySelectorAll(sel);
function createKeywordElement(tagName, className, text) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined && text !== null) element.textContent = text;
  return element;
}
const DEBUG_CLIENT = localStorage.getItem('debug_client') === '1';
function clientDebug(...args) {
  if (!DEBUG_CLIENT) return;
  console.debug(...args);
}

const KEYWORD_TABLE_ROW_HEIGHT = 42;
const KEYWORD_TABLE_OVERSCAN = 8;

function normalizeSourceKeyword(item, source = '') {
  const keyword = String(item?.keyword || item?.term || item?.word || item?.text || item || '').trim();
  if (!keyword) return null;
  return {
    keyword,
    searches30d: Number(item?.searches30d || item?.searches || item?.volume || item?.['30天搜索量'] || 0) || 0,
    source: String(source || item?.source || ''),
  };
}

function getLocalSearchTermSourceKeywords() {
  const library = getState('keywordLibrary');
  const marketDataKws = getMarketDataKeywords();
  const backendCandidates = Array.isArray(getState('stBackendCandidates'))
    ? getState('stBackendCandidates')
    : [];

  /** @type {Array<{keyword:string, searches30d:number, source:string}>} */
  const prioritized = backendCandidates
    .map((kw) => normalizeSourceKeyword(kw, 'backend-model'))
    .filter(Boolean);
  /** @type {Array<{keyword:string, searches30d:number, source:string}>} */
  let fallbackPool = [];

  if (library?.keywords) {
    const libKws = [...library.keywords]
      .sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0))
      .slice(0, 100)
      .map((k) => ({ ...k, source: 'library' }));
    fallbackPool = fallbackPool.concat(libKws);
  }

  if (marketDataKws.length > 0) {
    const existingSet = new Set([...prioritized, ...fallbackPool].map((k) => String(k.keyword || '').toLowerCase()));
    const newMarketDataKws = marketDataKws
      .filter((k) => !existingSet.has(extractKeywordText(k).toLowerCase()))
      .slice(0, 40)
      .map((k) => ({
        keyword: extractKeywordText(k),
        searches30d: extractSearchVolume(k),
        source: 'market-data',
      }));
    fallbackPool = fallbackPool.concat(newMarketDataKws);
  }

  if (prioritized.length === 0) {
    fallbackPool = fallbackPool
      .sort((a, b) => (b.searches30d || 0) - (a.searches30d || 0));
  }

  const allKeywords = prioritized.concat(fallbackPool);
  const seen = new Set();
  return allKeywords
    .filter((k) => {
      const key = String(k.keyword || '').toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 100);
}

function setBackendCandidates(candidateKeywords) {
  setState(
    'stBackendCandidates',
    (Array.isArray(candidateKeywords) ? candidateKeywords : [])
      .map((keyword) => normalizeSourceKeyword(keyword, 'backend-model'))
      .filter(Boolean)
  );
}

const searchTermsSyncController = createSearchTermsSyncController({
  getState,
  setState,
  updateSearchTerms: listingFacade.updateSearchTerms,
  normalizeSearchTermsDraft,
});

const AMAZON_DOMAINS = {
  US: 'amazon.com', DE: 'amazon.de', FR: 'amazon.fr',
  IT: 'amazon.it', ES: 'amazon.es', CA: 'amazon.ca',
  AU: 'amazon.com.au', UK: 'amazon.co.uk', JP: 'amazon.co.jp',
};

function amazonSearchUrl(keyword) {
  // 保留 wrapper：step2 模板里有些位置直接调用本函数；底层走共享实现
  return sharedAmazonSearchUrl(keyword);
}

// 注：getMarketDataKeywords 已迁到 keyword-lookup.js（state 别名兼容也在那）

function setMarketDataKeywords(list) {
  const next = Array.isArray(list) ? list : [];
  setState('marketDataKeywords', next);
  // Backward compatibility for Step3/optimization/sidebar code paths.
  setState('mcpKeywords', next);
}

const marketDataKeywordController = createMarketDataKeywordController({
  select: $,
  parseAsinsFromText,
  getErrorMessage,
  fetchMarketDataKeywords: marketDataKeywords,
  getMarketDataKeywords,
  extractKeywordText,
  setMarketDataKeywords,
});

const businessContextController = createBusinessContextController({
  select: $,
  getState,
  setState,
  createElement: createKeywordElement,
  onKeywordPolicyChanged: () => {
    void refreshKeywordStrategy({ silent: true });
  },
});
const { createPolicySelect } = businessContextController;

function getPolicyMap() {
  return new Map((getState('keywordUsePolicies') || []).map((item) => [String(item.keyword || '').toLowerCase(), item]));
}

function getSelectionMode() {
  return getState('stSelectionMode') === 'manual' ? 'manual' : 'ai';
}

const autoSearchTermsController = createSearchTermsAutoSelectionController({
  getState,
  setState,
  getSelectionMode,
  normalizeSearchTermsDraft,
  getLocalCandidates: getLocalSearchTermSourceKeywords,
  buildAutoSelection: buildAutoSelectedSearchTerms,
});

function setSelectionMode(mode, { forceAutoSelect = false } = {}) {
  const nextMode = mode === 'manual' ? 'manual' : 'ai';
  const prevMode = getSelectionMode();
  setState('stSelectionMode', nextMode);

  if (nextMode === 'ai' && (forceAutoSelect || prevMode !== 'ai')) {
    autoSearchTermsController.autoSelect({ force: true }).catch((err) => {
      clientDebug('[Step2] 切换到系统推荐失败:', err?.message || err);
      if (getSelectionMode() === 'ai') setState('stSelectionMode', 'manual');
      showToast('系统推荐暂不可用，已切换为手动选择。', { id: 'step2-keywords-toast', type: 'error' });
    });
  }
}

const stCandidateFilterController = createSearchTermsCandidateFilterController({
  select: $,
  selectAll: $$,
  getState,
  setState,
});

const stQuickSelectionController = createSearchTermsQuickSelectionController({
  selectAll: $$,
  getState,
  setState,
});

const searchTermsOutputController = createSearchTermsOutputController({
  select: $,
  getState,
  setState,
  setStateBulk,
  normalizeText,
  filterSelectedKeywords: filterSelectedKeywordsForSearchTerms,
  packKeywords: packKeywordsByBytes,
  maxBytes: MAX_SEARCH_TERMS_BYTES,
  normalizeDraft: normalizeSearchTermsDraft,
  updateByteCounter,
  renderSummary: renderKeywordSummaryInto,
  createElement: createKeywordElement,
  createTextNode: (text) => document.createTextNode(text),
  getSelectionMode,
});

// ── Keyword Table Rendering ─────────────────────────────────────

function renderKeywordTable() {
  renderKeywordTableView({
    getState,
    select: $,
    createElement: createKeywordElement,
    createPolicySelect,
    formatVolume,
    computeVirtualWindow: (options) => computeVirtualWindow(options),
    getPolicyMap,
    rowHeight: KEYWORD_TABLE_ROW_HEIGHT,
    overscan: KEYWORD_TABLE_OVERSCAN,
    afterRender: () => {
      renderStCandidates();
      businessContextController.render();
    },
  });
}

let keywordStrategyRefreshPromise = null;

function currentKeywordStrategy() {
  return getState('businessContext')?.keywordStrategyPackage || null;
}

function renderKeywordStrategy() {
  renderKeywordStrategyView($('#keyword-strategy-panel'), currentKeywordStrategy());
}

async function refreshKeywordStrategy({ silent = false } = {}) {
  const jobId = getState('batchJobId');
  if (!jobId) return currentKeywordStrategy();
  if (keywordStrategyRefreshPromise) return keywordStrategyRefreshPromise;
  const refreshButton = $('#refresh-keyword-strategy-button');
  if (refreshButton) {
    refreshButton.disabled = true;
    refreshButton.textContent = '刷新中…';
  }
  keywordStrategyRefreshPromise = previewBatchKeywordStrategy(jobId, {
    keywordUsePolicies: compactKeywordUsePoliciesForStep2(getState('keywordUsePolicies') || []),
  })
    .then((data) => {
      const prior = getState('businessContext') || {};
      const nextContext = {
        ...prior,
        generatedAt: data.generatedAt || prior.generatedAt,
        keywordStrategyPackage: data.keywordStrategyPackage || null,
        keywordPolicySummary: data.keywordPolicySummary || prior.keywordPolicySummary || null,
        keywordSemanticGroups: data.keywordSemanticGroups || prior.keywordSemanticGroups || {},
        keywordUsePolicies: Array.isArray(data.keywordUsePolicies)
          ? data.keywordUsePolicies
          : (prior.keywordUsePolicies || []),
      };
      setState('businessContext', nextContext);
      renderKeywordStrategy();
      return nextContext.keywordStrategyPackage;
    })
    .catch((error) => {
      if (!silent) showToast(`关键词策略刷新失败：${getErrorMessage(error)}`);
      throw error;
    })
    .finally(() => {
      keywordStrategyRefreshPromise = null;
      const button = $('#refresh-keyword-strategy-button');
      if (button) {
        button.disabled = false;
        button.textContent = '刷新策略';
      }
    });
  return keywordStrategyRefreshPromise;
}

// ── Search Terms Candidates ──────────────────────────────────────

function renderStCandidates() {
  const container = $('#st-candidates-list');
  if (!container) return;
  stCandidateFilterController.sync();

  const selected = getState('selectedStKeywords') || new Set();
  const showingZh = !!getState('searchTermsShowingZh');
  const translating = !!getState('searchTermsTranslationLoading');
  const translatedItems = Array.isArray(getState('stCandidatesTranslationItems'))
    ? getState('stCandidatesTranslationItems')
    : [];

  const allKeywords = getLocalSearchTermSourceKeywords();

  const sourceSnapshot = allKeywords.map((k) => `${k.keyword}::${k.source || ''}`).join('|');
  const prevSnapshot = getState('stCandidatesSourceSnapshot') || '';
  if (sourceSnapshot !== prevSnapshot) {
    setState('stCandidatesSourceSnapshot', sourceSnapshot);
    setState('stCandidatesSourceKeywords', allKeywords.map((k) => ({ keyword: k.keyword, source: k.source })));
    setState('stCandidatesTranslationItems', []);
    setState('stCandidatesTranslationMap', {});
    setState('searchTermsTranslationWarning', '');
    setState('searchTermsShowingZh', false);
  }

  // 准备每条 item 在中文理解模式下的翻译映射（按下标查 translatedItems）
  const translateMap = new Map();
  if (showingZh) {
    allKeywords.forEach((k, idx) => {
      const zh = translatedItems[idx];
      if (zh) translateMap.set(String(k.keyword || '').toLowerCase(), zh);
    });
  }

  const filter = stCandidateFilterController.getFilterState();
  const categoryMap = stCandidateFilterController.getCategoryMap();
  const filteredKeywords = allKeywords.filter((item) =>
    stCandidateFilterController.matches(
      String(item?.keyword || ''),
      item,
      selected,
      categoryMap,
      filter
    )
  );

  // 走共享 chip 渲染器；step2 特有的 source class 和 Amazon 搜索 ↗ 通过 options 传入
  renderKeywordChips(container, filteredKeywords, {
    selectedSet: selected,
    onToggle: (kw, checked, item) => searchTermsOutputController.handleCheckboxChange({
      target: { dataset: { keyword: kw }, checked },
      // 兼容旧 handler 可能用到的额外字段
      currentTarget: { dataset: { keyword: kw } },
      item,
    }, { getSelectionMode }),
    showSearchLink: true,
    translateMap,
    translatedView: showingZh,
    sourceClassResolver: (item) => item.source === 'market-data' ? 'market-data-kw mcp-kw' : '',
  });

  const countEl = $('#st-candidate-count');
  if (countEl) {
    countEl.textContent = `${filteredKeywords.length} / ${allKeywords.length}`;
  }

  searchTermsOutputController.renderTranslationStatus(translating ? '正在翻译候选词...' : '');

  // 注：checkbox 事件绑定已由 renderKeywordChips 完成，不再在此重复绑
  // 原 kwSourceMap 死代码也一并清掉（没人消费）

  searchTermsOutputController.update();
}

function handleTabClick(e) {
  const tab = e.target.closest('.keyword-tab');
  if (!tab) return;

  $$('.keyword-tab').forEach((t) => t.classList.remove('active'));
  tab.classList.add('active');
  setState('keywordFilterCategory', tab.dataset.category || 'all');
}

let filterDebounce = null;
function handleFilterInput(e) {
  if (filterDebounce) clearTimeout(filterDebounce);
  filterDebounce = setTimeout(() => {
    setState('keywordFilterText', e.target.value || '');
  }, 250);
}

function handleSelectionModeClick(mode) {
  if (mode === 'ai') {
    setSelectionMode('ai', { forceAutoSelect: true });
    return;
  }
  setSelectionMode('manual');
}

function handleSummaryModeClick(e) {
  const aiBtn = e.target.closest('#st-mode-ai');
  if (aiBtn) {
    handleSelectionModeClick('ai');
    return;
  }
  const manualBtn = e.target.closest('#st-mode-manual');
  if (manualBtn) {
    handleSelectionModeClick('manual');
  }
}

function hasMeaningfulSearchTermTranslation(translatedItems, keywords) {
  return translatedItems.some((item, idx) => {
    const translated = normalizeText(item);
    if (!translated) return false;
    const source = normalizeText(keywords[idx]?.keyword || '');
    return translated.toLowerCase() !== source.toLowerCase() || /[\u4e00-\u9fff]/.test(translated);
  });
}

async function toggleSearchTermsTranslation() {
  const keywords = Array.isArray(getState('stCandidatesSourceKeywords'))
    ? getState('stCandidatesSourceKeywords')
    : [];
  if (!keywords.length) return;

  const showingZh = !!getState('searchTermsShowingZh');
  if (showingZh) {
    setState('searchTermsShowingZh', false);
    setState('searchTermsTranslationError', '');
    setState('searchTermsTranslationWarning', '');
    renderStCandidates();
    return;
  }

  const cachedMap = getState('stCandidatesTranslationMap') || {};
  if (Object.keys(cachedMap).length > 0) {
    setState('searchTermsShowingZh', true);
    renderStCandidates();
    return;
  }

  setState('searchTermsTranslationLoading', true);
  setState('searchTermsTranslationError', '');
  setState('searchTermsTranslationWarning', '');
  searchTermsOutputController.update();

  try {
    const data = await translateSearchTerms({ items: keywords.map((k) => k.keyword) });
    const rawTranslatedItems = Array.isArray(data.translatedItems) ? data.translatedItems : [];
    const translatedItems = keywords.map((_, idx) => normalizeText(rawTranslatedItems[idx] || ''));
    if (!hasMeaningfulSearchTermTranslation(translatedItems, keywords)) {
      throw new Error('翻译结果为空或格式异常');
    }

    // Create lowercase keyword to Chinese translation Map for instant lookup
    const map = {};
    keywords.forEach((k, idx) => {
      if (translatedItems[idx]) {
        map[k.keyword.toLowerCase()] = translatedItems[idx];
      }
    });

    setState('stCandidatesTranslationItems', translatedItems);
    setState('stCandidatesTranslationMap', map);
    const missingCount = translatedItems.filter((item) => !item).length;
    const warnings = Array.isArray(data.warnings) ? data.warnings.filter(Boolean) : [];
    setState(
      'searchTermsTranslationWarning',
      missingCount > 0 || warnings.length
        ? `部分候选词未返回中文，${missingCount || '少量'} 个缺失项已保留原文。`
        : ''
    );
    setState('searchTermsShowingZh', true);
  } catch (err) {
    setState('searchTermsTranslationError', getErrorMessage(err));
    setState('searchTermsTranslationWarning', '');
    setState('searchTermsShowingZh', false);
  } finally {
    setState('searchTermsTranslationLoading', false);
    renderStCandidates();
  }
}

// ── Keyword Stats ───────────────────────────────────────────────

function renderKeywordStats() {
  const library = getState('keywordLibrary');
  if (!library) return;

  const total = library.keywords?.length || 0;
  const cat = library.categories || {};
  const core = cat.coreKeywords?.length || 0;
  const longTail = cat.longTailKeywords?.length || 0;
  const painPoint = cat.painPointKeywords?.length || 0;
  const scene = cat.sceneKeywords?.length || 0;
  const competitor = cat.competitorKeywords?.length || 0;

  const stats = $('#keyword-stats');
  if (stats) {
    const values = [
      [total, '总关键词', ''],
      [core, '核心词', 'chip-strong'],
      [longTail, '长尾词', ''],
      [painPoint, '痛点词', ''],
      [scene, '场景词', ''],
      [competitor, '竞品词', ''],
    ];
    stats.replaceChildren(...values.map(([count, label, extraClass]) => {
      const chip = createKeywordElement('div', `chip${extraClass ? ` ${extraClass}` : ''}`);
      chip.append(createKeywordElement('strong', '', count), document.createTextNode(` ${label}`));
      return chip;
    }));
  }
  // The business-context renderer moved into its controller. Keep the stats
  // refresh on that same controller path so restored keyword libraries do not
  // abort the remaining application bootstrap with a stale function reference.
  businessContextController.render();
  renderKeywordStrategy();
}

// ── Export ───────────────────────────────────────────────────────

function exportKeywordLibrary(format) {
  const library = getState('keywordLibrary');
  if (!library) return;

  if (format === 'csv') {
    const header =
      'keyword,searches30d,cpc,purchaseRate,competition,frequency,relevanceScore,source';
    const rows = library.keywords.map((k) =>
      [
        k.keyword,
        k.searches30d,
        k.cpc,
        k.purchaseRate,
        k.competition,
        k.frequency,
        k.relevanceScore,
        k.source,
      ].join(',')
    );
    downloadText([header, ...rows].join('\n'), 'keyword-library.csv', 'text/csv');
  } else {
    downloadText(
      JSON.stringify(library, null, 2),
      'keyword-library.json',
      'application/json'
    );
  }
}

async function exportKeywordStrategyWorkbook() {
  const existing = currentKeywordStrategy();
  if (!existing && !getState('batchJobId')) {
    showToast('请先完成竞品分析，再导出策略工作簿。');
    return;
  }
  try {
    const strategy = await refreshKeywordStrategy({ silent: false }) || existing;
    if (!strategy) throw new Error('策略词库尚未就绪');
    downloadKeywordStrategyWorkbook(strategy);
    showToast('已导出 Excel 兼容的多工作表策略词库。');
  } catch {
    // refreshKeywordStrategy already reports the actionable reason.
  }
}

function downloadText(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Module Init ─────────────────────────────────────────────────

export function initStep2() {
  // ── Tab Filtering ────────────────────────────────────────────
  $('#keyword-tabs')?.addEventListener('click', handleTabClick);
  $('#keyword-filter')?.addEventListener('input', handleFilterInput);
  stCandidateFilterController.bind();
  stQuickSelectionController.bind();
  $('#keyword-summary-view')?.addEventListener('click', handleSummaryModeClick);

  // ── Export Buttons ───────────────────────────────────────────
  $('#export-csv-button')?.addEventListener('click', () =>
    exportKeywordLibrary('csv')
  );
  $('#export-json-button')?.addEventListener('click', () =>
    exportKeywordLibrary('json')
  );
  $('#export-strategy-workbook-button')?.addEventListener('click', () => {
    void exportKeywordStrategyWorkbook();
  });
  $('#keyword-strategy-panel')?.addEventListener('click', (event) => {
    if (!event.target.closest('#refresh-keyword-strategy-button')) return;
    void refreshKeywordStrategy();
  });

  // ── ST Builder Buttons ────────────────────────────────────────
  $('#fetch-hot-keywords-btn')?.addEventListener('click', marketDataKeywordController.fetchHotKeywords);
  $('#expand-keywords-btn')?.addEventListener('click', marketDataKeywordController.expandKeywordsFromAsin);
  $('#add-manual-keyword-btn')?.addEventListener('click', marketDataKeywordController.toggleManualKeywordInput);
  $('#manual-keyword-confirm')?.addEventListener('click', marketDataKeywordController.commitManualKeywords);
  $('#manual-keyword-cancel')?.addEventListener('click', marketDataKeywordController.hideManualKeywordInput);
  $('#translate-searchterms')?.addEventListener('click', toggleSearchTermsTranslation);
  $('#fill-st-button')?.addEventListener('click', () => searchTermsOutputController.fillToBudget(240));
  $('#copy-st-button')?.addEventListener('click', () => searchTermsOutputController.copyCurrent());
  $('#clear-st-button')?.addEventListener('click', () => {
    if (!confirm('确定要清空已选关键词吗？此操作不可撤销。')) return;
    setState('stSelectionMode', 'manual');
    setState('selectedStKeywords', new Set());
    setBackendCandidates([]);
    setMarketDataKeywords([]);
  });

  // ── Store Subscriptions (Reactive UI) ──────────────────────────
  subscribe('state:keywordLibraryChanged', () => {
    renderKeywordStats();
    renderKeywordTable();
    if (getState('currentStep') === 2 && getSelectionMode() === 'ai' && !autoSearchTermsController.hasSelection()) {
      autoSearchTermsController.autoSelect().catch((err) => {
        clientDebug('[Step2] 词库更新后自动推荐失败:', err?.message || err);
      });
    }
  });

  subscribe('state:keywordUsePoliciesChanged', () => {
    renderKeywordStats();
    renderKeywordTable();
  });

  subscribe('state:businessContextChanged', () => {
    businessContextController.render();
    renderKeywordStrategy();
  });

  subscribe('state:competitorRolesChanged', () => {
    businessContextController.render();
  });

  subscribe('state:marketDataKeywordsChanged', () => {
    renderStCandidates();
  });
  subscribe('state:mcpKeywordsChanged', () => {
    renderStCandidates();
  });
  subscribe('state:stBackendCandidatesChanged', () => {
    renderStCandidates();
  });
  subscribe('state:stCandidateFilterCategoryChanged', () => {
    renderStCandidates();
  });
  subscribe('state:stCandidateFilterTextChanged', () => {
    renderStCandidates();
  });

  subscribe('state:keywordFilterCategoryChanged', () => {
    renderKeywordTable();
  });

  subscribe('state:keywordFilterTextChanged', () => {
    renderKeywordTable();
  });

  subscribe('state:selectedStKeywordsChanged', () => {
    keywordPrecheckController.invalidateForSelectionChange();
    renderStCandidates(); // Re-render tags to reflect checked state
    searchTermsSyncController.schedule();
  });
  subscribe('state:stSelectionModeChanged', () => {
    renderStCandidates();
  });

  // ── Reset auto-select flag when a new batch job starts ──────────
  subscribe('state:batchJobIdChanged', ({ curr }) => {
    autoSearchTermsController.reset();
    keywordPrecheckController.reset();
    setBackendCandidates([]);
    searchTermsSyncController.clear();
    setStateBulk({
      stCandidateFilterCategory: 'all',
      stCandidateFilterText: '',
      stSelectionMode: 'ai',
    });
  });

  subscribe('state:currentStepChanged', ({ curr }) => {
    if (curr === 2) {
      autoSearchTermsController.autoSelect().catch((err) => {
        clientDebug('[Step2] 自动生成搜索词失败:', err?.message || err);
      });
    }
  });

  // ── Initial Render ───────────────────────────────────────────────
  renderKeywordStats();
  renderKeywordTable();
  renderStCandidates();
  searchTermsOutputController.update();
  autoSearchTermsController.autoSelect().catch((err) => {
    clientDebug('[Step2] 初始自动生成搜索词失败:', err?.message || err);
  });

  // ── Keyword Conflict Modal Events (register once) ────────────────
  initKeywordConflictModalEvents();
  initKeywordReviewModalEvents();

  // ── Next/Prev Step Buttons ───────────────────────────────────────
  $('#step2-prev-button')?.addEventListener('click', () => setState('currentStep', 1));
  $('#step2-next-button')?.addEventListener('click', async (event) => {
    const maxNav = getMaxNavigableStep();
    if (maxNav < 3) {
      showToast('请等词库构建完成并确定好搜索词后再进入下一步', { id: 'step2-keywords-toast', type: 'error' });
      return;
    }
    await keywordPrecheckController.start(event.currentTarget);
  });
}

// ── Keyword Conflict Modal ─────────────────────────────────────────

// 💡 事件在 initStep2 入口统一注册一次，不重复绑定，杜绝 once:true 死锁
function initKeywordConflictModalEvents() {
  const modal = $('#keyword-conflict-modal');
  const backBtn = $('#conflict-back-btn');
  const proceedBtn = $('#conflict-proceed-btn');

  // 💡 DOM 0 级 .onclick 覆盖写，天然幂等，彻底断绝重复监听
  if (backBtn) {
    backBtn.onclick = () => {
      closeModalDialog(modal);
    };
  }

  if (proceedBtn) {
    proceedBtn.onclick = () => {
      if (!modal) return;
      closeModalDialog(modal);
      keywordPrecheckController.proceed();
    };
  }
}

function initKeywordReviewModalEvents() {
  const modal = $('#keyword-review-modal');
  const backBtn = $('#keyword-review-back-btn');
  const retryBtn = $('#keyword-review-retry-btn');

  if (backBtn) backBtn.onclick = keywordPrecheckController.hideReviewModal;
  if (retryBtn) {
    retryBtn.onclick = async () => {
      keywordPrecheckController.hideReviewModal();
      await keywordPrecheckController.start();
    };
  }
}

// 💡 专门负责动态渲染的渲染器，只读数据，不负责绑定任何长事件
function showKeywordConflictModal(conflicts, result = {}, trigger) {
  const modal = $('#keyword-conflict-modal');
  const listEl = $('#conflict-list');
  const sourceEl = $('#conflict-facts-source');
  if (!modal || !listEl) return;

  if (sourceEl) {
    sourceEl.textContent = `本次按${result?.factsSourceLabel || '产品事实'}判断`;
  }

  listEl.replaceChildren(...conflicts.map((conflict) => {
    const item = createKeywordElement('div', 'keyword-conflict-item');
    const head = createKeywordElement('div', 'keyword-conflict-item-head');
    head.append(
      createKeywordElement('span', 'keyword-conflict-status', '已移除'),
      createKeywordElement('strong', 'keyword-conflict-keyword', conflict.keyword),
      createKeywordElement('span', 'keyword-conflict-mismatch', '与卖点不一致:'),
      createKeywordElement('strong', 'keyword-conflict-selling-point', `"${conflict.sellingPoint || ''}"`),
    );
    const reason = createKeywordElement('div', 'keyword-conflict-reason');
    reason.append(createKeywordElement('strong', '', '移除原因：'), document.createTextNode(conflict.reason || ''));
    item.append(head, reason);
    return item;
  }));

  openModalDialog(modal, { onRequestClose: () => closeModalDialog(modal), trigger });
}

function showKeywordReviewModal(result, trigger) {
  const modal = $('#keyword-review-modal');
  const message = $('#keyword-review-message');
  if (!modal) return;
  if (message) {
    message.textContent = result?.warning || '关键词检测暂时不可用，请重新检测。';
  }
  openModalDialog(modal, { onRequestClose: () => keywordPrecheckController.hideReviewModal(), trigger });
}

const keywordPrecheckController = createKeywordPrecheckController({
  select: $,
  getState,
  setState,
  preCheckKeywords,
  getErrorMessage,
  clientDebug,
  updateStOutput: searchTermsOutputController.update,
  showConflictModal: showKeywordConflictModal,
  showReviewModal: showKeywordReviewModal,
  showModalStatus,
  hideModalStatus,
  closeModalDialog,
});
